"use client";

import React, {useEffect, useRef, useState} from "react";
import {cn} from "@/lib/utils";
import Image, {StaticImageData} from "next/image";
import UnicornScene from "unicornstudio-react";

export default function UnicornWrapper({
  jsonFilePath,
  placeholderImage,
  className,
}: {
  jsonFilePath: string;
  placeholderImage: StaticImageData;
  className?: string;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hasError, setHasError] = useState(false);

  const handleError = (error: Error) => {
    console.error("Unicorn scene failed to load:", error);
    setHasError(true);
  };

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          } else {
            setIsVisible(false);
          }
        });
      },
      {
        root: null,
        rootMargin: "-0px",
        threshold: 0.01,
      }
    );

    observer.observe(el);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className={cn(
        "absolute inset-0 w-full h-full",
        className
      )}
    >
      {hasError || !isVisible ? (
        <Image
          className="!w-full !h-full object-cover"
          src={placeholderImage}
          alt=""
        />
      ) : null}

      {!hasError && isVisible && (
        <UnicornScene
          jsonFilePath={jsonFilePath}
          width="100%"
          height="100%"
          fps={60}
          lazyLoad={true}
          placeholder=""
          onError={handleError}
          showPlaceholderOnError={false}
          className={cn(
            "!w-full !h-full",
            !isVisible && "opacity-0"
          )}
        />
      )}
    </div>
  );
}
